import BrandHeroBlock from '@/blocks/BrandHeroBlock';
import BreadcrumbsBlock from '@/blocks/BreadcrumbsBlock';
import TextImageAsideBlock from '@/blocks/TextImageAsideBlock';
import TextImageAsideDarkBlock from '@/blocks/TextImageAsideDarkBlock';
import FancyBoxHeading from '@/components/FancyBoxHeading';
import Prose from '@/components/Prose';
import { MEDIA_URL, REVALIDATE_TIME_IN_SEC } from '@/constants/env';
import { fadeIn, fadeInUp } from '@/constants/motions';
import { pages } from '@/content';
import FullWidthLayout from '@/layouts/FullWidthLayout';
import Image from '@/ui/Image';
import { Icon } from '@iconify/react';
import { motion } from 'framer-motion';
import Link from 'next/link';
import graphql from '@/lib/graphql';
import mainLayout from '@/graphql/queries/mainLayout';
import Markdown from 'react-markdown';
import page from '@/graphql/queries/page';
import FancyHeading from '@/components/FancyHeading';
import TestimonialBox from '@/components/TestimonialBox';

export default function AboutPage({ mainLayout, page }) {
  let { contentSection, metaData } = page.attributes;
  return (
    <FullWidthLayout mainLayout={mainLayout} metaData={metaData}>
      <BrandHeroBlock
        image={MEDIA_URL.concat(contentSection[0].heroImage.data.attributes.url)}
        title={contentSection[0].heroTitle}
      />
      <BreadcrumbsBlock items={[pages.home, pages.about]} />
      <motion.section
        className="bg-primary-700/10 px-4 py-16"
        initial="hidden"
        transition={{
          ...fadeIn.transition,
          when: 'beforeChildren',
          staggerChildren: 0.4,
        }}
        variants={fadeIn}
        viewport={fadeIn.viewport}
        whileInView="visible"
      >
        <div className="container-screen">
          <div className="flex flex-col items-center gap-y-4 md:flex-row">
            <motion.div className="h-72 w-full shrink-0 md:h-[600px] md:w-1/2" variants={fadeInUp}>
              <Image
                alt="Image"
                className="h-full w-full overflow-hidden rounded-xl object-cover shadow-lg"
                src={MEDIA_URL.concat(contentSection[1].ourCompaniesImage.data.attributes.url)}
              />
            </motion.div>
            <div className="flex flex-col items-center md:ml-8 md:items-start">
              <motion.div
                className="relative z-10 inline-block rounded-md bg-primary-500 px-16 py-1 md:-ml-16"
                variants={fadeInUp}
              >
                <h2 className="text-2xl text-white md:text-4xl">
                  {contentSection[1].ourCompaniesTitle}
                </h2>
              </motion.div>
              <Prose className="mt-6" variants={fadeInUp}>
                {contentSection[1].companies.map(({ text, url }, i) => (
                  <p key={i}>
                    <Markdown>{text}</Markdown>
                    <Link href={url}>
                      <a>Read More</a>
                    </Link>
                  </p>
                ))}
              </Prose>
            </div>
          </div>
        </div>
      </motion.section>
      <motion.section
        className="my-16 px-4"
        initial="hidden"
        transition={{
          ...fadeIn.transition,
          when: 'beforeChildren',
          staggerChildren: 0.4,
        }}
        variants={fadeIn}
        viewport={fadeIn.viewport}
        whileInView="visible"
      >
        <div className="container-screen">
          <motion.header className="text-center" variants={fadeInUp}>
            <h2 className="text-3xl md:text-4xl">{contentSection[2].coreValuesTitle}</h2>
            <p className="mt-4 text-base font-medium text-primary-500 md:text-lg">
              {contentSection[2].subtitle}
            </p>
          </motion.header>
          <div className="mt-8 flex flex-wrap gap-8">
            {contentSection[2].values.map(({ title, icon }, i) => (
              <motion.div
                key={i}
                className="flex flex-1 flex-col items-center text-center"
                variants={fadeInUp}
              >
                <Icon className="text-primary-500" height="96" icon={icon} />
                <h4 className="mt-4 font-semibold uppercase">{title}</h4>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>
      <TextImageAsideDarkBlock
        image={MEDIA_URL.concat(contentSection[3].textImageAsideImage.data.attributes.url)}
        imageDirection="right"
        title={contentSection[3].textImageAsideTitle}
      >
        <Markdown>{contentSection[3].textImageAsideText}</Markdown>
      </TextImageAsideDarkBlock>
      <TextImageAsideBlock
        image={MEDIA_URL.concat(contentSection[4].textImageAsideImage.data.attributes.url)}
        imageClassName="lg:aspect-square"
        imageDirection="left"
        title={contentSection[4].textImageAsideTitle}
      >
        <Markdown>{contentSection[4].textImageAsideText}</Markdown>
      </TextImageAsideBlock>
      <TextImageAsideBlock
        image={MEDIA_URL.concat(contentSection[5].textImageAsideImage.data.attributes.url)}
        imageClassName="lg:-mt-24"
        imageDirection="right"
        sectionClassName="bg-primary-700/10 pt-16 !mb-0"
        title={<FancyBoxHeading>{contentSection[5].textImageAsideTitle}</FancyBoxHeading>}
      >
        <Markdown>{contentSection[5].textImageAsideText}</Markdown>
      </TextImageAsideBlock>
      <TextImageAsideBlock
        image={MEDIA_URL.concat(contentSection[6].textImageAsideImage.data.attributes.url)}
        imageClassName="lg:aspect-square"
        imageDirection="left"
        sectionClassName="bg-primary-700/10 py-16 !mt-0 !mb-0"
        title={<FancyBoxHeading>{contentSection[6].textImageAsideTitle}</FancyBoxHeading>}
      >
        <Prose className="marker:text-neutral-400">
          <Markdown>{contentSection[6].textImageAsideText}</Markdown>
        </Prose>
      </TextImageAsideBlock>
      <motion.section
        className="my-16 px-4"
        initial="hidden"
        transition={{
          ...fadeIn.transition,
          when: 'beforeChildren',
          staggerChildren: 0.4,
        }}
        variants={fadeIn}
        viewport={fadeIn.viewport}
        whileInView="visible"
      >
        <div className="container-screen">
          <FancyHeading variants={fadeInUp}>{contentSection[7].testimonialTitle}</FancyHeading>
          <div className="mt-8 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {contentSection[7].testimonials.map((testimonial, i) => (
              <TestimonialBox variants={fadeInUp} {...testimonial} key={i} />
            ))}
          </div>
        </div>
      </motion.section>
    </FullWidthLayout>
  );
}

export async function getStaticProps() {
  const { data: mainLayoutData } = await graphql(mainLayout);

  const { data: pageData } = await graphql(page, { slug: 'about' });

  return {
    props: {
      mainLayout: mainLayoutData.mainLayout.data,
      page: pageData.pages.data[0],
    },
    revalidate: parseInt(REVALIDATE_TIME_IN_SEC), // In seconds
  };
}
